import { DeleteIcon } from '@chakra-ui/icons'
import { Button, Flex, Heading, HStack, Text, Tooltip } from '@chakra-ui/react'
import type { FormikState } from 'formik'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import type { SourcesResponse } from '../../interfaces'
import { MainContentHeadingBox } from '../../layouts/Base/MainContent'
import PermissionGuard from '../PermissionGuard'
import ExternalStorageIcon from './ExternalStorageIcon'
import { sourceTextMapping } from './utils'

interface Props {
  sourceId: string
  values: SourcesResponse
  dirty: boolean
  isSubmitting: boolean
  loadingSources: boolean
  setFormError: React.Dispatch<React.SetStateAction<string>>
  onOpenDeleteOrigin(): void
  resetForm(nextState?: Partial<FormikState<SourcesResponse>> | undefined): void
}

const ExternalStorageHeader = ({
  sourceId,
  values,
  dirty,
  isSubmitting,
  loadingSources,
  onOpenDeleteOrigin,
  setFormError,
  resetForm,
}: Props): ReactElement => {
  const { t } = useTranslation()

  return (
    <MainContentHeadingBox>
      {sourceId && sourceId !== 'new' ? (
        <Flex alignItems="center">
          <ExternalStorageIcon
            typeId={Number(values.typeId)}
            baseUrl={values.baseUrl ?? ''}
            loadingSources={loadingSources}
          />
          <Flex
            flexDirection="column"
            justify="center"
            marginLeft={4}
            maxWidth={80}
            // TODO : check whether maxWidth here is correct
          >
            <Tooltip label={values.name}>
              <Text
                fontSize="lg"
                fontWeight="semibold"
                color={colors.appClearBlue}
                lineHeight="taller"
                isTruncated
              >
                {values.name}
              </Text>
            </Tooltip>

            <Text
              fontSize="md"
              fontWeight="normal"
              color={colors.appSlate}
              lineHeight="none"
            >
              {sourceTextMapping[values.typeId]}
            </Text>
          </Flex>
        </Flex>
      ) : (
        <Heading as="h2" size="md" fontWeight="semibold" color="appDarkIndigo">
          {t('new_origin', 'New origin')}
        </Heading>
      )}
      <PermissionGuard
        permission={PermissionsEnum.SELF_INTEGRATION_MANAGE}
      >
        {
          (hasPermission: boolean) => (
            hasPermission ? (
              <HStack spacing={4}>
                {sourceId && sourceId !== 'new' && (
                  <Button
                    leftIcon={<DeleteIcon />}
                    variant="deleteCTA"
                    onClick={onOpenDeleteOrigin}
                  >
                    {t('delete_origin', 'Delete Origin')}
                  </Button>
                )}
                {dirty && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFormError('')
                      resetForm()
                    }}
                  >
                    {t('cancel', 'Cancel')}
                  </Button>
                )}
                <Button
                  type="submit"
                  isDisabled={!dirty}
                  isLoading={isSubmitting}
                  loadingText="Saving"
                >
                  {t('save', 'Save')}
                </Button>
              </HStack>
            ) : <></>
          )
        }
      </PermissionGuard>
    </MainContentHeadingBox>
  )
}

export default ExternalStorageHeader
